import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpParams, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';

import {OperationCredit} from './operation-credit/operation-credit.model';
import {GlobalVariable} from '../../global';
import {Operation, UserPartnerConfirmation} from './operation.model';
import {OperationDebit} from './operation-debit/operation-debit.model';
import {Customer} from '../../customer/customer.model';
import {RequestParameters} from '../../shared/models/request-parameters/parameter.model';
import {CustomerService} from '../../customer/customer.service';
import {forkJoin} from 'rxjs/observable/forkJoin';
import { DatePipe } from '@angular/common';
import { OperationDepotCollect } from './operation-callcenter-depot-collect/operation-callcenter-depot-collect.model';

@Injectable()
export class OperationService {

  userPartnerConfirmation: UserPartnerConfirmation;

  private subject = new Subject<any>();

  public menuButtonDisable = new Subject<any>();

  constructor(private http: HttpClient,
              private customerService: CustomerService) {

  }

  checkUserPartner (userPartnerConfirmation: UserPartnerConfirmation) {

    const params = new HttpParams()
      .set('reference', userPartnerConfirmation.userOperatorPartner)
      .set('secretCode', userPartnerConfirmation.userOperatorPartnerSecretCode.replace(/\*/gi, 'X'));

    return this.http.get(GlobalVariable.BASE_API_URL + '/operation/operator-partner/check', {
      params
    })
      .map(
        (response: HttpResponse<any>) => {
          return response;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error.status,
            content: JSON.parse(error.error).message
          });
        }
      );

  }

  postMassOperationCredit(operation: OperationCredit) {
    const body = this.normalizeOperationData(operation);

    return this.http.post(GlobalVariable.BASE_API_URL + '/operations/credit', body)
      .map(
        (response: HttpResponse<any>) => {
          return response ;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error,
            message: JSON.parse(error.error).message
          });
        }
      );
  }

  postOperationCredit(operation: OperationCredit) {
    const body = this.normalizeOperationData(operation);

    return this.http.post(GlobalVariable.BASE_API_URL + '/operations/credit', body)
      .map(
        (response: HttpResponse<any>) => {
          return response ;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error,
            message: JSON.parse(error.error).message
          });
        }
      );
  }

  postOperationDebit(operation: OperationDebit) {
    const body = this.normalizeOperationData(operation);

    return this.http.post(GlobalVariable.BASE_API_URL + '/operations/debit', body)
      .map(
        (response: HttpResponse<any>) => {
          return response ;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error,
            message: JSON.parse(error.error).message
          });
        }
      );
  }

  postOperationTransfert(operation: Operation) {
    const body = this.normalizeOperationData(operation);

    return this.http.post(GlobalVariable.BASE_API_URL + '/operations/transfert', body)
      .map(
        (response: HttpResponse<any>) => {
          return response ;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error,
            message: JSON.parse(error.error).message
          });
        }
      );
  }

  postOperationDepotCollect(operation: OperationDepotCollect) {
    const body = this.normalizeOperationDepotCollectData(operation);

    // console.log(body);

    const datePipe = new DatePipe('en');

    if (body.cardExpiration) {
      body.cardExpiration  = datePipe.transform(body.cardExpiration, 'yyyy-MM-dd');
    }

    return this.http.post(GlobalVariable.BASE_COLLECT_API_URL + '/operations/depot-collect', body)
      .map(
        (response: HttpResponse<any>) => {
          return response ;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error,
            message: JSON.parse(error.error).message
          });
        }
      );
  }

  postConfirmOperation(operation: Operation, confirmationCode: string) {
    const body = this.normalizeOperationProcessData(operation, confirmationCode);

    return this.http.post(GlobalVariable.BASE_API_URL + '/operations/confirm', body)
      .map(
        (response: HttpResponse<any>) => {
          return response ;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error,
            message: JSON.parse(error.error).message
          });
        }
      );
  }

  postManagerConfirmOperation(operation: Operation, confirmationCode: string) {
    const body =  {
      'operation': operation.reference,
      'confirmationCode': confirmationCode
    };

    return this.http.post(GlobalVariable.BASE_API_URL + '/operations/manager-confirm', body)
      .map(
        (response: HttpResponse<any>) => {
          return response ;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error,
            message: JSON.parse(error.error).message
          });
        }
      );
  }

  postCancelOperation(operation: Operation, confirmationCode: string = '') {
    const body = this.normalizeOperationProcessData(operation, confirmationCode);
    // cancel an operation without the code;
    delete body.confirmationCode;

    return this.http.post(GlobalVariable.BASE_API_URL + '/operations/cancel', body)
      .map(
        (response: HttpResponse<any>) => {
          return response ;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error,
            message: JSON.parse(error.error).message
          });
        }
      );
  }

  getResendManagerConfirmationCode(operation: Operation) {
    return this.http.get(GlobalVariable.BASE_API_URL + '/operation-manager-confirm/' + operation.reference + '/send-confirmation-code')
      .map(
        (response: HttpResponse<any>) => {
          return response ;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error,
            message: JSON.parse(error.error).message
          });
        }
      );
  }

  getResendConfirmationCode(operation: Operation) {
    return this.http.get(GlobalVariable.BASE_API_URL + '/operation/' + operation.reference + '/resend-sms')
      .map(
        (response: HttpResponse<any>) => {
          return response ;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error,
            message: JSON.parse(error.error).message
          });
        }
      );
  }

  getOngoingCustomerOperations(customer: Customer, parameters: RequestParameters) {

    return this.http.get(GlobalVariable.BASE_API_URL + '/operations/ongoing/' + customer.phone, {
      params: parameters.getHttpParams()
    } )
      .map(
        (response: HttpResponse<any>) => {
          return response;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error,
            message: JSON.parse(error.error).message
          });
        }
      );
  }

  getOperation(operationReference: string) {
    return this.http.get(GlobalVariable.BASE_API_URL + '/operation/' + operationReference)
      .map(
        (response: HttpResponse<any>) => {
          return response;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error.status,
            message: JSON.parse(error.error).message
          });
        }
      );
  }
  getMfsOperation(operationReference: string) {
    return this.http.get(GlobalVariable.BASE_API_URL + '/thirdparty/operation/' + operationReference)
      .map(
        (response: HttpResponse<any>) => {
          return response;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error.status,
            message: JSON.parse(error.error).message
          });
        }
      );
  }
  getTpoDetails(operationReference: string){
    return this.http.get(GlobalVariable.BASE_API_URL + '/thirdparty/operation/' + operationReference)
    .map(
      (response: HttpResponse<any>) => {
        return response;
      }
    ).catch(
      (error: HttpErrorResponse) => {
        return Observable.throw({
          code: error.status,
          message: JSON.parse(error.error).message
        });
      }
    );
  }
  cancelMfsOperation(operationReference){
    return this.http.post(GlobalVariable.BASE_API_URL + '/thirdparty/operation/cancel/' + operationReference,{})
    .map(
      (response: HttpResponse<any>) => {
        return response;
      }
    ).catch(
      (error: HttpErrorResponse) => {
        return Observable.throw({
          code: error.status,
          message: JSON.parse(error.error).message
        });
      }
    );
  }
  cancelTpoOperation(operationReference){
    return this.http.post(GlobalVariable.BASE_API_URL + '/thirdparty/operation/cancel/' + operationReference,{})
    .map(
      (response: HttpResponse<any>) => {
        return response;
      }
    ).catch(
      (error: HttpErrorResponse) => {
        return Observable.throw({
          code: error.status,
          message: JSON.parse(error.error).message
        });
      }
    );
  }
  getOperationWithCustomers(operationReference: string) {
    return this.http.get(GlobalVariable.BASE_API_URL + '/operation/' + operationReference)
      .mergeMap(
        (operation: Operation) => {
          const customerRequest: Observable<Customer> = this.customerService.getCustomer(operation.account.accountNumber);
          let receiverRequest: Observable<Customer> = Observable.of(null);
          let senderRequest: Observable<Customer> = Observable.of(null);

          if (operation.receiverAccount !== null) {
            receiverRequest =  this.customerService.getCustomer(operation.receiverAccount.accountNumber);
          }

          if (operation.status === 'CONFIRMED' && operation.sendingOperation !== null) {
            senderRequest =  this.customerService.getCustomer(operation.sendingOperation.account.accountNumber);
          }

          return forkJoin([customerRequest, receiverRequest, senderRequest]).map(
            (result: Customer[]) => {
              operation.customer = result[0];
              operation.receiver = result[1];
              operation.sender = result[2];
              return operation;
            }
          );

        }
      ).map(
        (response: Operation) => {
          return response;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error.status,
            message: JSON.parse(error.error).message
          });
        }
      );
  }

  calculateDepotCollectOperationCost(operationCostCalculate: any) {
    return this.http.post(GlobalVariable.BASE_COLLECT_API_URL + '/operations/calculate-operation-cost',
      operationCostCalculate
    ).map(
      (response: any) => {
        return response;
      }
    );
  }

  calculateOperationCost(operationCostCalculate: any) {
    return this.http.post(GlobalVariable.BASE_API_URL + '/operations/calculate-operation-cost',
      operationCostCalculate
    ).map(
      (response: any) => {
        return response;
      }
    );
  }

  convertAmount(data: {amount: number, sourceCountry: string, targetCountry: string}) {
    return this.http.post(GlobalVariable.BASE_API_URL + '/operations/convert/amount',
      data
    ).map(
      (response: any) => {
        return response;
      }
    );
  }
 /* getOperationTpoDetail(reference:string){
    return this.http.get (GlobalVariable.BASE_API_URL+'/operation/thirdparty/{reference}')
  }*/
  getOperationTpoDetail(reference: string) {
    return this.http.get(GlobalVariable.BASE_API_URL + '/thirdparty/' + reference + '/operation')
      .map(
        (response: HttpResponse<any>) => {
          return response;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error.status,
            message: JSON.parse(error.error).message
          });
        }
      );
  }

  getValueAddedServiceOperationDetail(reference: string, operationType: string, provider: string) {
    const params = new HttpParams()
      .set('reference', reference)
      .set('operationType', operationType)
      .set('provider', provider);

    return this.http.get(GlobalVariable.BASE_API_URL + '/thirdparty/operation', { params })
      .map(
        (response: HttpResponse<any>) => {
          return response;
        }
      ).catch(
        (error: HttpErrorResponse) => {
          return Observable.throw({
            code: error.status,
            message: JSON.parse(error.error).message
          });
        }
      );
  }

  private normalizeOperationDepotCollectData(operation: OperationDepotCollect): OperationDepotCollect {
    const body: OperationDepotCollect = Object.assign({}, operation);
    delete body.reference;
    delete body.operationCost;
    delete body.status;
    delete body.operationType;
    delete body.operationAmountOnAccount;
    delete body.receivingAmount;

    delete body.customer.country;
    delete body.customer.status;
    delete body.customer.username;
    delete body.customer.role;
    delete body.customer.account;
    delete body.customer.lastConnexion;
    delete body.customer.lastChangePassword;
    delete body.customer.platform;
    delete body.customer.lastConnexionCountry;
    delete body.customer.lastConnexionIp;
    delete body.customer.name;
    delete body.customer.cardExpiration;
    delete body.customer.reference;
    delete body.customer.city;
    delete body.customer.picture;
    delete body.customer.cashOutMaximumAmount;
    delete body.customer.operationRate;
    delete body.customer.doubleAuthenticationRequired;

    if (body.collectAgency !== null) {
      body.collectAgency = body.collectAgency.reference;
    }

    if (body.productDetails !== null) {
      if (body.productDetails.length) {
        body.productDetails = body.productDetails.map(
          (prd) => {
            return {
              product: prd.product.reference,
              amount: prd.amount
            }
          }
        )
      }
    }

    if (body.userOperatorPartner !== null) {
      body.userOperatorPartner = body.userOperatorPartner.reference;
    }

    body.userOperatorPartner = this.userPartnerConfirmation.userOperatorPartner.reference;
    body.userOperatorPartnerSecretCode = this.userPartnerConfirmation.userOperatorPartnerSecretCode.replace(/\*/gi, 'X');

    return body;
  }

  private normalizeOperationData(operation: Operation): Operation {
    const body: Operation = Object.assign({}, operation);
    delete body.reference;
    delete body.operationCost;
    delete body.status;
    delete body.operationAmountOnAccount;
    delete body.receivingAmount;
    delete body.operationType;
    delete body.customer.country;
    delete body.customer.status;
    delete body.customer.username;
    delete body.customer.role;
    delete body.customer.account;
    delete body.customer.lastConnexion;
    delete body.customer.lastChangePassword;
    delete body.customer.platform;
    delete body.customer.lastConnexionCountry;
    delete body.customer.lastConnexionIp;
    delete body.customer.notificationType;

    const datePipe = new DatePipe('en');

    if (body.cardExpiration) {
      body.cardExpiration  = datePipe.transform(body.cardExpiration, 'yyyy-MM-dd');
    }

    if (body.receiver !== null && body.receiver !== undefined) {
      delete body.receiver.country;
      delete body.receiver.status;
      delete body.receiver.username;
      delete body.receiver.role;
      delete body.receiver.account;
      delete body.receiver.lastConnexion;
      delete body.receiver.lastChangePassword;
      delete body.receiver.platform;
      delete body.receiver.lastConnexionCountry;
      delete body.receiver.lastConnexionIp;
      if (body.receiver.cardExpiration) {
        body.receiver.cardExpiration  = datePipe.transform(body.receiver.cardExpiration, 'yyyy-MM-dd');
      }
    }

    if (body.customer !== null) {
      delete body.customer.reference;
      if (body.customer.city !== null) {
        body.customer.city = body.customer.city.reference;
      }
      if (body.customer.picture !== null) {
        body.customer.picture = body.customer.picture.reference;
      }
    }

    if (body.receiver !== null && body.receiver !== undefined) {
      delete body.receiver.reference;
      if (body.receiver.city !== null) {
        body.receiver.city = body.receiver.city.reference;
      }
      if (body.receiver.picture !== null) {
        body.receiver.picture = body.receiver.picture.reference;
      }
    }

    if (body.userOperatorPartner !== null) {
      body.userOperatorPartner = body.userOperatorPartner.reference;
    }

    body.userOperatorPartner = this.userPartnerConfirmation.userOperatorPartner.reference;
    body.userOperatorPartnerSecretCode = this.userPartnerConfirmation.userOperatorPartnerSecretCode.replace(/\*/gi, 'X');

    return body;
  }

  private normalizeOperationProcessData(operation: Operation, confirmationCode: string) {
    return {
      'operation': operation.reference,
      'confirmationCode': confirmationCode,
      'userOperatorPartner': this.userPartnerConfirmation.userOperatorPartner.reference,
      'userOperatorPartnerSecretCode': this.userPartnerConfirmation.userOperatorPartnerSecretCode.replace(/\*/gi, 'X')
    };
  }

  emitMessage(msg: object) {
    this.subject.next(msg);
  }

  getMessage(): Observable<any> {
    return this.subject.asObservable();
  }

}



// WEBPACK FOOTER //
// ./src/app/operation/operations/operation.service.ts